"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { getCreateDoseFormValues, getCreateDoseMedicationDefaults, nearestAppointmentLocationId, resolveCreateMedication, toCreateDosePayload } from "@/components/dose/dose-form/create-dose-model";
import DoseComponents from "@/components/dose/dose-form/dose-components";
import { DoseEventFields, DoseProviderField } from "@/components/dose/dose-form/dose-event-fields";
import { DoseFormValues, doseFormSchema } from "@/components/dose/dose-form/dose-form-schema";
import { useDoseComponentInventory } from "@/components/dose/dose-form/use-dose-component-inventory";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useTenant } from "@/contexts/tenant-provider";
import { useToast } from "@/contexts/toaster";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { getDoseCommentLabel } from "@/lib/tenant-flags";
import { Medication, MedicationSummary } from "@/types/medication";
import { NoteDetail, NoteListItem } from "@/types/notes";
import { assertDoseLotResolution } from "./dose-form/dose-lot-resolution";
import { useCreateMedicationMutation } from "@/api/provider/user/patient/patient-mutation";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
import { medicationsQueryOptions } from "@/query/medications/medications";
interface AddDoseProps {
  patientId: string;
  selectedNote?: NoteListItem | NoteDetail | null;
  /** Appointment the dose is logged against, e.g. from the appointment checklist. */
  eventId?: string | null;
  selectedMedicationType?: string;
  mostRecentDose?: MedicationSummary | null;
  onSubmit: (data: MedicationSummary) => void;
  children: React.ReactNode;
}
interface DuplicateDoseProps {
  patientId: string;
  selectedNote?: NoteListItem | NoteDetail | null;
  /** Appointment the dose is logged against, e.g. from the appointment checklist. */
  eventId?: string | null;
  sourceDose: MedicationSummary;
  onSubmit: (data: MedicationSummary) => void;
  children: React.ReactNode;
}
interface CreateDoseDialogProps {
  patientId: string;
  selectedNote?: NoteListItem | NoteDetail | null;
  /** Appointment the dose is logged against, e.g. from the appointment checklist. */
  eventId?: string | null;
  onSubmit: (data: MedicationSummary) => void;
  children: React.ReactNode;
  title: string;
  description: string;
  notice?: React.ReactNode;
  getInitialValues: (medications: Medication[]) => DoseFormValues;
  eventFieldPolicy?: "template" | "fallback-to-initial" | "preserve";
}
const EMPTY_MEDICATIONS: Medication[] = [];
const FORM_ID = "add-dose-form";
function CreateDoseDialog({
  patientId,
  selectedNote,
  eventId,
  onSubmit,
  children,
  title,
  description,
  notice,
  getInitialValues,
  eventFieldPolicy = "template"
}: CreateDoseDialogProps) {
  const [open, setOpen] = useState(false);
  const {
    currentUser,
    currentLocationId
  } = useUser();
  // Stock comes out at the appointment's location, not the location picked in
  // the sidebar: the dose's appointment, else the patient's appointment from the
  // last 24 hours or later today nearest the time the dialog opened, else the
  // sidebar location. Both lookups share the cache of the screens around the
  // dialog, so they rarely hit the network.
  const clock = useClinicClock();
  const [openedAt, setOpenedAt] = useState<{
    now: Date;
    endOfToday: Date;
  } | null>(null);
  const appointmentId = eventId ?? selectedNote?.eventId ?? null;
  const {
    data: appointment,
    isLoading: isAppointmentLoading
  } = useQuery({
    ...eventQueryOptions.detail(appointmentId ?? ""),
    enabled: open && !!appointmentId
  });
  const {
    data: patientAppointments,
    isLoading: isNearbyLoading
  } = useQuery({
    ...eventQueryOptions.searchPatientEvents(patientId, ""),
    enabled: open && !appointmentId
  });
  const isLocationLoading = isAppointmentLoading || isNearbyLoading;
  const doseLocationId = (appointmentId ? appointment?.location?.id : openedAt && nearestAppointmentLocationId(patientAppointments ?? [], openedAt.now, openedAt.endOfToday)) || currentLocationId;
  const {
    tenant
  } = useTenant();
  const {
    toast
  } = useToast();
  const addMedicationMutation = useCreateMedicationMutation();
  const {
    data: allMedications = EMPTY_MEDICATIONS,
    isPending
  } = useQuery(medicationsQueryOptions.list());
  const getDialogInitialValues = useCallback((medications: Medication[]): DoseFormValues => ({
    ...getInitialValues(medications),
    providerId: currentUser?.id
  }), [currentUser?.id, getInitialValues]);
  const form = useForm<DoseFormValues>({
    resolver: zodResolver(doseFormSchema),
    defaultValues: getDialogInitialValues(allMedications),
    mode: "onSubmit"
  });
  const needsCatalogHydration = useRef(false);
  const components = useWatch({
    control: form.control,
    name: "components"
  });
  // Subscribe during render so catalog hydration can distinguish fields the
  // user has edited from untouched defaults.
  const {
    dirtyFields
  } = form.formState;
  const providerId = useWatch({
    control: form.control,
    name: "providerId"
  });
  const canChangeProvider = hasPermission(currentUser, PERMISSIONS.PATIENTS_DOSES_PROVIDER_WRITE);
  const compoundMedicationId = useWatch({
    control: form.control,
    name: "compoundMedicationId"
  });
  const inventory = useDoseComponentInventory({
    components,
    compoundMedicationId: compoundMedicationId ?? null,
    medications: allMedications,
    resolveMedication: resolveCreateMedication,
    currentLocationId: doseLocationId,
    providerId: providerId || currentUser?.id,
    patientId,
    enabled: open,
    isPendingMedications: isPending,
    isLocationPending: isLocationLoading
  });
  const handleMedicationChange = useCallback((index: number, name: string) => {
    const defaults = getCreateDoseMedicationDefaults(name, allMedications);
    // Keep any amount or quantity the user typed; only untouched numbers take
    // the medication's defaults.
    const current = form.getValues(`components.${index}`);
    const component = {
      ...defaults.component,
      typed: current.typed,
      amount: current.typed?.amount ? current.amount : defaults.component.amount,
      quantity: current.typed?.quantity ? current.quantity : defaults.component.quantity
    };
    form.setValue(`components.${index}`, component, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
    // Updating the field-array row does not notify the nested controlled lot
    // selector, so update that path explicitly to keep its visible value in sync.
    form.setValue(`components.${index}.lotSelection`, defaults.component.lotSelection, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });

    // Secondary components never replace shared details. Duplicated doses keep
    // their source details even when the first medication changes.
    if (index !== 0 || eventFieldPolicy === "preserve") return;
    const initialValues = eventFieldPolicy === "fallback-to-initial" ? getInitialValues(allMedications) : undefined;
    const eventFieldNames = ["administrationLocation", "comment", "frequency"] as const;
    for (const fieldName of eventFieldNames) {
      if (!dirtyFields[fieldName]) {
        form.setValue(fieldName, defaults.eventFields[fieldName] || initialValues?.[fieldName] || "", {
          shouldValidate: true
        });
      }
    }
  }, [allMedications, dirtyFields, eventFieldPolicy, form, getInitialValues]);
  useEffect(() => {
    if (!open || isPending || !needsCatalogHydration.current) return;
    needsCatalogHydration.current = false;
    const initialValues_0 = getDialogInitialValues(allMedications);

    // Hydrate once when a catalog that was unavailable at open time arrives.
    // Preserve the opening date and dirty user input; later catalog refreshes
    // must not regenerate the form or replace template-derived event details.
    form.reset({
      ...initialValues_0,
      doseDate: form.getValues("doseDate")
    }, {
      keepDirty: true,
      keepDirtyValues: true,
      keepTouched: true
    });
  }, [allMedications, form, getDialogInitialValues, isPending, open]);
  const handleOpenChange = (isOpen: boolean) => {
    needsCatalogHydration.current = isOpen && isPending;
    if (isOpen) {
      form.reset(getDialogInitialValues(allMedications));
    }
    setOpenedAt(isOpen ? {
      now: new Date(),
      endOfToday: clock.dayRange(clock.today()).end
    } : null);
    setOpen(isOpen);
  };
  const handleProviderChange = useCallback(() => {
    components.forEach((component_0, index_0) => {
      if (!component_0.lotSelection) return;
      form.setValue(`components.${index_0}.lotSelection`, null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
    });
  }, [components, form]);
  const handleFormSubmit = async (values: DoseFormValues) => {
    if (inventory.isCheckingInventory || addMedicationMutation.isPending) return;
    for (const [index_1, component_1] of values.components.entries()) {
      try {
        assertDoseLotResolution(component_1, inventory.entries[index_1] ?? {
          shipments: []
        });
      } catch (error) {
        form.setError(`components.${index_1}.lotSelection`, {
          message: error instanceof Error ? error.message : "Resolve this lot before saving."
        });
        return;
      }
    }
    if (!currentUser) {
      toast({
        title: "Authentication Error",
        description: "There is an authentication error with your user. Please log out and log back in.",
        variant: "error"
      });
      return;
    }
    try {
      const result = await addMedicationMutation.mutateAsync(toCreateDosePayload(values, {
        patientId,
        selectedNote,
        eventId,
        providerId: values.providerId || currentUser.id,
        currentLocationId: doseLocationId,
        inventory: inventory.entries.map(entry => ({
          ...entry,
          medicationId: entry.selectedMedication?.id
        }))
      }));
      if (result) {
        onSubmit(result);
        setOpen(false);
      } else {
        toast({
          title: "Error",
          description: "Failed to save dose. Please try again.",
          variant: "error"
        });
      }
    } catch {
      // Optimistic update, rollback, and error toast handled by the mutation.
    }
  };
  if (!hasPermission(currentUser, PERMISSIONS.PATIENTS_DOSES_CREATE)) {
    return null;
  }
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <FormProvider {...form}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <DialogBody>
            {notice}
            <form id={FORM_ID} onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
              {canChangeProvider && <DoseProviderField label="Prescribing Provider" onValueChange={handleProviderChange} />}
              <DoseComponents inventory={inventory.entries} medications={allMedications} onMedicationChange={handleMedicationChange} />
              <DoseEventFields commentLabel={getDoseCommentLabel(tenant)} />
            </form>
          </DialogBody>

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form={FORM_ID} disabled={components.length === 0} isLoading={addMedicationMutation.isPending || inventory.isCheckingInventory} loadingText={inventory.isCheckingInventory ? "Checking inventory..." : "Saving..."}>
              Save Dose
            </Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </Dialog>;
}
export default function AddDose(t0) {
  const $ = _c(14);
  let mostRecentDose;
  let props;
  let selectedMedicationType;
  if ($[0] !== t0) {
    ({
      selectedMedicationType,
      mostRecentDose,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = mostRecentDose;
    $[2] = props;
    $[3] = selectedMedicationType;
  } else {
    mostRecentDose = $[1];
    props = $[2];
    selectedMedicationType = $[3];
  }
  let t1;
  if ($[4] !== mostRecentDose || $[5] !== selectedMedicationType) {
    t1 = medications => getCreateDoseFormValues({
      selectedMedicationType,
      mostRecentDose,
      medications
    });
    $[4] = mostRecentDose;
    $[5] = selectedMedicationType;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const getInitialValues = t1;
  let t2;
  if ($[7] !== mostRecentDose) {
    t2 = mostRecentDose ? <Alert variant="default" className="mb-4"><AlertTitle>Pre-filled with previous dose information</AlertTitle><AlertDescription>We've filled in the form with details from the patient's last dose to save you time. Please review and update the component amounts, administration location, and any current side effects before saving.</AlertDescription></Alert> : undefined;
    $[7] = mostRecentDose;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = mostRecentDose ? "fallback-to-initial" : "template";
  let t4;
  if ($[9] !== getInitialValues || $[10] !== props || $[11] !== t2 || $[12] !== t3) {
    t4 = <CreateDoseDialog {...props} title="Add New Dose" description="Record a dose event for this patient. Add one component for a normal dose or multiple components for a compound dose." notice={t2} getInitialValues={getInitialValues} eventFieldPolicy={t3} />;
    $[9] = getInitialValues;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function DuplicateDose(t0) {
  const $ = _c(9);
  let props;
  let sourceDose;
  if ($[0] !== t0) {
    ({
      sourceDose,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = sourceDose;
  } else {
    props = $[1];
    sourceDose = $[2];
  }
  let t1;
  if ($[3] !== sourceDose) {
    t1 = medications => getCreateDoseFormValues({
      sourceDose,
      medications
    });
    $[3] = sourceDose;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getInitialValues = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Alert variant="default" className="mb-4"><AlertTitle>Duplicating dose</AlertTitle><AlertDescription>This form has been pre-filled with information from the selected dose event. Please review the components before saving.</AlertDescription></Alert>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== getInitialValues || $[7] !== props) {
    t3 = <CreateDoseDialog {...props} title="Duplicate Dose" description="Duplicating an existing dose event. Review and update the shared event details and components as needed." notice={t2} getInitialValues={getInitialValues} eventFieldPolicy="preserve" />;
    $[6] = getInitialValues;
    $[7] = props;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}